import {
  Container,
  Icon,
  SliderWithInput as Slider,
  Spinner,
  TypoGraph,
  Toast as toast,
} from 'components'
import styles from './radiusOfOperationFilter.module.scss'
import { Button } from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { getRadiusSettings, updateRadiusSettings } from 'api'
import { reportException } from 'tools/logs'
import strings from 'strings'

interface IRadiusOfOperationFilterProps {
  onButtonClick: () => void
  reloadDashboard: () => void
  useKilometers?: boolean
  /**
   * When false, Garaging Zip Code Configuration stays visible but is disabled and the click
   * handler no-ops (NB fleet ROO). Insurer default ROO keeps true.
   */
  garagingZipcodeConfigurationEnabled?: boolean
  /** Label for the Garaging Zip Code Configuration button. Defaults to 'Garaging Zip Code'. */
  garagingZipcodeLabel?: string
  /** Carrier identity — pass from useCarrierCodeBootstrap so the filter re-fires when data arrives. Falls back to localStorage. */
  carrierId?: string
  carrierCodeType?: string
  carrierCodeValue?: string
  /** When true, sliders are disabled (used to enforce read-only mode, e.g. RBAC VIEW_ONLY in the insurer carrier-details flow). */
  readOnly?: boolean
}

const GLOBAL_MIN = 0
const GAP = 0.1
const KM_FACTOR = 1.60934
const HARDCODED_DEFAULTS_MILES = [100, 250, 500, 1000]

const RADIUS_SETTING_KEYS = ['radius1', 'radius2', 'radius3', 'radius4'] as const

/** Stable rounding for display and API payloads (avoids float noise e.g. from miles→km). */
function roundToDecimalPlaces(n: number, decimalPlaces: number): number {
  const f = 10 ** decimalPlaces
  return Math.round(n * f) / f
}

/**
 * Round radius1–radius4 from GET to one decimal for display (miles and km).
 * Avoids long float noise from the API or unit conversions.
 */
function normalizeRadiusSettingsResponse(response: any): any {
  if (!response || typeof response !== 'object') {
    return response
  }
  const next = { ...response }
  for (const k of RADIUS_SETTING_KEYS) {
    const v = next[k]
    if (typeof v === 'number' && Number.isFinite(v)) {
      next[k] = roundToDecimalPlaces(v, 1)
    } else if (typeof v === 'string' && v.trim() !== '') {
      const parsed = parseFloat(v)
      if (Number.isFinite(parsed)) {
        next[k] = roundToDecimalPlaces(parsed, 1)
      }
    }
  }
  return next
}

const round1 = (n: number) => roundToDecimalPlaces(n, 1)

function cascade(values: number[], changedIdx: number, globalMax: number): number[] {
  const r = [...values]

  for (let i = changedIdx + 1; i < r.length; i++) {
    if (r[i] <= r[i - 1]) {
      r[i] = round1(r[i - 1] + GAP)
    }
  }

  if (r[r.length - 1] > globalMax) {
    r[r.length - 1] = globalMax
    for (let i = r.length - 2; i >= 0; i--) {
      if (r[i] >= r[i + 1]) {
        r[i] = round1(r[i + 1] - GAP)
      }
    }
  }

  for (let i = changedIdx - 1; i >= 0; i--) {
    if (r[i] >= r[i + 1]) {
      r[i] = round1(r[i + 1] - GAP)
    }
  }

  if (r[0] < GLOBAL_MIN) {
    r[0] = GLOBAL_MIN
    for (let i = 1; i < r.length; i++) {
      if (r[i] <= r[i - 1]) {
        r[i] = round1(r[i - 1] + GAP)
      }
    }
  }

  return r
}

const RadiusOfOperationFilter: React.FC<IRadiusOfOperationFilterProps> = (
  props
) => {
  const {
    onButtonClick,
    reloadDashboard,
    useKilometers = false,
    garagingZipcodeConfigurationEnabled = true,
    garagingZipcodeLabel = strings.SUMMARY_REPORT.RADIUS_OF_OPERATION.GARAGING_ZIPCODE_CONFIG,
    carrierId: carrierIdProp,
    carrierCodeType: carrierCodeTypeProp,
    carrierCodeValue: carrierCodeValueProp,
    readOnly = false,
  } = props

  const resolvedCarrierId = carrierIdProp ?? localStorage.getItem('carrierId') ?? ''
  const resolvedCodeType = carrierCodeTypeProp ?? localStorage.getItem('carrierCodeType') ?? ''
  const resolvedCodeValue = carrierCodeValueProp ?? localStorage.getItem('carrierCodeValue') ?? ''
  const distanceUnit = useKilometers ? 'Km' : 'Miles'
  const GLOBAL_MAX = useKilometers ? 4000 : 2500
  const companyRadiusDefaults = useSelector((state: any) => state?.companyConfig?.companyRadiusDefaults)
  const defaultRadii = companyRadiusDefaults
    ? [companyRadiusDefaults.radius1, companyRadiusDefaults.radius2, companyRadiusDefaults.radius3, companyRadiusDefaults.radius4]
        .map(r => round1(useKilometers ? r * KM_FACTOR : r))
    : HARDCODED_DEFAULTS_MILES.map(r => useKilometers ? round1(r * KM_FACTOR) : r)

  const [radiusSettings, setRadiusSettings] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [radii, setRadii] = useState<number[] | null>(null)
  const [sliderSnapKeys, setSliderSnapKeys] = useState([0, 0, 0, 0])

  const effectiveRadii: number[] = radii ?? [
    radiusSettings?.radius1 ?? defaultRadii[0],
    radiusSettings?.radius2 ?? defaultRadii[1],
    radiusSettings?.radius3 ?? defaultRadii[2],
    radiusSettings?.radius4 ?? defaultRadii[3],
  ]

  const callGetRadiusSettings = useCallback((silent = false, onDone?: () => void) => {
    if (!resolvedCodeType || !resolvedCodeValue) return
    if (!silent) setIsLoading(true)
    getRadiusSettings(resolvedCarrierId, resolvedCodeType, resolvedCodeValue, useKilometers)
      .then((response: any) => {
        setRadiusSettings(normalizeRadiusSettingsResponse(response))
        setRadii(null)
      })
      .catch((error: any) => {
        reportException(error)
      })
      .finally(() => {
        if (!silent) setIsLoading(false)
        onDone?.()
      })
  }, [resolvedCarrierId, resolvedCodeType, resolvedCodeValue, useKilometers])

  useEffect(() => {
    callGetRadiusSettings()
  }, [callGetRadiusSettings])

  const saveRadiusSettings = useCallback(
    (r: number[]) => {
      const payload = {
        radius1: round1(r[0]).toFixed(1),
        radius2: round1(r[1]).toFixed(1),
        radius3: round1(r[2]).toFixed(1),
        radius4: round1(r[3]).toFixed(1),
      }
      updateRadiusSettings(payload, resolvedCarrierId, resolvedCodeType, resolvedCodeValue, useKilometers)
        .then((response: any) => {
          toast({
            id: 'updateRadiusSettings',
            type: 'success',
            subTitle: response?.message,
          })
        })
        .catch((error: any) => {
          reportException(error)
        })
        .finally(() => {
          callGetRadiusSettings(true, reloadDashboard)
        })
    },
    [callGetRadiusSettings, reloadDashboard]
  )

  const handleGaragingConfigClick = useCallback(() => {
    if (!garagingZipcodeConfigurationEnabled) {
      return
    }
    onButtonClick()
  }, [garagingZipcodeConfigurationEnabled, onButtonClick])

  const handleRadiusChange = useCallback(
    (index: number, value: number) => {
      const current = [...effectiveRadii]
      current[index] = value
      const newRadii = cascade(current, index, GLOBAL_MAX)
      if (newRadii.every((v, i) => v === effectiveRadii[i])) {
        setSliderSnapKeys((keys) => keys.map((k, ki) => (ki === index ? k + 1 : k)))
        return
      }
      setRadii(newRadii)
      saveRadiusSettings(newRadii)
    },
    [effectiveRadii, saveRadiusSettings]
  )

  const sliderTitles = ['Radius 1', 'Radius 2', 'Radius 3', 'Radius 4']
  const sliderTestIds = [
    'radius-slider-1',
    'radius-slider-2',
    'radius-slider-3',
    'radius-slider-4',
  ]

  return (
    <Container className={styles.filterContainer} data-testid="radius-of-operation-filter">
      <Container className={styles.sliderContainer}>
        {(isLoading && <Spinner />) || (
          <>
            {effectiveRadii.map((value, i) => (
              <Slider
                key={`${i}-${sliderSnapKeys[i]}`}
                initialValue={value}
                min={GLOBAL_MIN}
                max={GLOBAL_MAX}
                maxInputLength={5}
                step={0.1}
                color="primary"
                useInputLabel={true}
                title={sliderTitles[i]}
                label={distanceUnit}
                testId={sliderTestIds[i]}
                disabled={readOnly}
                onValueChange={(v) => handleRadiusChange(i, v)}
              />
            ))}
          </>
        )}
      </Container>
      <Button
        className={styles.configButton}
        disabled={!garagingZipcodeConfigurationEnabled}
        onClick={handleGaragingConfigClick}
        data-testid="config-button"
      >
        <Icon materialIcon="Settings" size={30} />
        <TypoGraph variant="body1" align="center">
          {garagingZipcodeLabel}
          <br />
          Configuration
        </TypoGraph>
      </Button>
    </Container>
  )
}
export default RadiusOfOperationFilter